import axios from 'utils/axios';
import { apiEndpoint } from 'config/api.config';
import { PaginatedResponse } from '../models/Pagination';
import { AttachmentType } from 'data/AttachmentType';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { telemetryService } from './telemetryService';

export interface StatusDto {
  id: string;
  name: string;
  description: string;
}

export interface CommentDto {
  id: string;
  customerId: string;
  createdDate: string;
  commentText: string;
}

export interface OrderItemSummaryDto {
  id: string;
  orderItemReference: string;
  productName: string;
  createdOn: string;
  status: StatusDto;
}

export interface HistoricOrderItemSummaryDto extends OrderItemSummaryDto {
  completedBy: string;
  qualityControlledBy: string;
  statusCreatedOn: Date;
}

export interface OnHoldOrderItemSummaryDto extends OrderItemSummaryDto {
  onHoldReason: string;
  onHoldDate: Date;
}

export interface OrderItemDto extends OrderItemSummaryDto {
  comments: CommentDto[];
  data: string;
  canRegenerateReport: boolean;
}

export interface UpdateOrderItemDto {
  statusId?: string;
  data?: string;
  completedBy?: string;
  qualityControlledBy?: string;
}

export interface UpdateOrderItemResult {
  etag: string | null;
  lockOwnerId: string | null;
}

export interface OrderItemEditLeaseDto {
  id: string;
  orderItemId: string;
  userId: string;
  acquiredAt: string;
  renewedAt: string;
  expiresAt: string;
  releasedAt?: string | null;
}

export interface CustomerUploadedPlansAttachmentDto {
  attachmentUris: string[];
  canReplace: boolean;
  isOverride: boolean;
}

const IF_MATCH_HEADER = 'If-Match';
const EDIT_LEASE_ID_HEADER = 'Edit-Lease-Id';

interface RequestHeaderOptions {
  etag?: string | null;
  editLeaseId?: string | null;
}

const extractETag = (headers: any): string | null => {
  let etagHeader: string | null = null;

  if (typeof headers?.get === 'function') {
    const headerValue = headers.get('etag');
    etagHeader = typeof headerValue === 'string' ? headerValue : null;
  } else if (headers?.etag) {
    etagHeader = typeof headers.etag === 'string' ? headers.etag : null;
  }

  return etagHeader ? etagHeader.replace(/^"|"$/g, '') : null;
};

const buildRequestConfig = (config: any, options: RequestHeaderOptions = {}) => {
  const headers: Record<string, string> = {
    ...(config.headers as Record<string, string>)
  };

  if (options.etag) {
    headers[IF_MATCH_HEADER] = `"${options.etag}"`;
  }

  if (options.editLeaseId) {
    headers[EDIT_LEASE_ID_HEADER] = options.editLeaseId;
  }

  return {
    ...config,
    headers
  };
};

const getBase64ByteLength = (value: string): number => {
  const base64Value = value.includes(',') ? value.split(',').pop() ?? '' : value;
  const normalizedValue = base64Value.replace(/\s/g, '');

  if (!normalizedValue) {
    return 0;
  }

  const paddingLength = normalizedValue.endsWith('==') ? 2 : normalizedValue.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((normalizedValue.length * 3) / 4) - paddingLength);
};

const orderRepository = {
  getPaginatedOrderItems: async <T>(params = {}): Promise<PaginatedResponse<T>> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItems');

    try {
      telemetryService.trackTrace('Fetching paginated order items', SeverityLevel.Verbose, { params });

      const response = await axios.get(url, { ...config, params });

      telemetryService.trackDependency(
        `GET_orderItems_${Date.now()}`,
        'GET',
        url,
        '/api/orderitems',
        Date.now() - startTime,
        true,
        response.status
      );

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `GET_orderItems_${Date.now()}`,
        'GET',
        url,
        '/api/orderitems',
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'getPaginatedOrderItems',
        params,
        statusCode: error.status,
        message: error.message
      });

      console.error('Error retrieving paginated items from Order Service', error);
      throw error;
    }
  },

  getOrderItem: async (id: string): Promise<{ orderItem: OrderItemDto; etag: string | null }> => {
  const startTime = Date.now();
  const { url, config } = apiEndpoint('orderItem', { id });

  try {
    telemetryService.trackTrace(`Fetching order item: ${id}`, SeverityLevel.Verbose);

    const response = await axios.get(url, config);

    const etag = extractETag(response.headers);

    telemetryService.trackDependency(
      `GET_orderItem_${Date.now()}`,
      'GET',
      url,
      `/api/orderitems/${id}`,
      Date.now() - startTime,
      true,
      response.status
    );

    telemetryService.trackEvent('OrderItemViewed', {
      orderId: id,
      status: response.data.status?.name,
      productName: response.data.productName,
      hasETag: !!etag
    });

    return { orderItem: response.data, etag };
  } catch (error: any) {
    telemetryService.trackDependency(
      `GET_orderItem_${Date.now()}`,
      'GET',
      url,
      `/api/orderitems/${id}`,
      Date.now() - startTime,
      false,
      error.status || 0
    );

    telemetryService.trackException(error, SeverityLevel.Error, {
      operation: 'getOrderItem',
      orderId: id,
      statusCode: error.status
    });

    console.error('Error retrieving OrderItem from Order Service', error);
    throw error;
  }
},

  regenerateReport: async (orderItemId: string): Promise<void> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemRegenerateReport', { id: orderItemId });

    try {
      const response = await axios.post(url, null, config);

      telemetryService.trackDependency(
        `POST_reportRegeneration_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/regenerate-report`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.trackEvent('ReportRegenerationRequested', {
        orderId: orderItemId
      });
    } catch (error: any) {
      telemetryService.trackDependency(
        `POST_reportRegeneration_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/regenerate-report`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, error.status === 409 || error.status === 403 ? SeverityLevel.Warning : SeverityLevel.Error, {
        operation: 'regenerateReport',
        orderId: orderItemId,
        statusCode: error.status
      });

      console.error('Error requesting report regeneration from Order Service', error);
      throw error;
    }
  },

  acquireOrderItemEditLease: async (orderItemId: string, existingLeaseId?: string | null): Promise<OrderItemEditLeaseDto> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemEditLease', { id: orderItemId });

    try {
      const response = await axios.post(url, null, buildRequestConfig(config, { editLeaseId: existingLeaseId }));

      telemetryService.trackDependency(
        `POST_orderItemEditLease_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/edit-lease`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.trackEvent('OrderItemEditLeaseAcquired', {
        orderId: orderItemId,
        renewedExistingLease: !!existingLeaseId
      });

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `POST_orderItemEditLease_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/edit-lease`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, error.status === 409 ? SeverityLevel.Warning : SeverityLevel.Error, {
        operation: 'acquireOrderItemEditLease',
        orderId: orderItemId,
        statusCode: error.status,
        isLockConflict: error.status === 409
      });

      console.error('Error acquiring OrderItem edit lease from Order Service', error);
      throw error;
    }
  },

  renewOrderItemEditLease: async (orderItemId: string, leaseId: string): Promise<OrderItemEditLeaseDto> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemEditLeaseHeartbeat', { id: orderItemId, leaseId });

    try {
      const response = await axios.put(url, null, buildRequestConfig(config));

      telemetryService.trackDependency(
        `PUT_orderItemEditLeaseHeartbeat_${Date.now()}`,
        'PUT',
        url,
        `/api/orderitems/${orderItemId}/edit-lease/${leaseId}/heartbeat`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.trackEvent('OrderItemEditLeaseRenewed', {
        orderId: orderItemId
      });

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `PUT_orderItemEditLeaseHeartbeat_${Date.now()}`,
        'PUT',
        url,
        `/api/orderitems/${orderItemId}/edit-lease/${leaseId}/heartbeat`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Warning, {
        operation: 'renewOrderItemEditLease',
        orderId: orderItemId,
        statusCode: error.status
      });

      console.error('Error renewing OrderItem edit lease from Order Service', error);
      throw error;
    }
  },

  releaseOrderItemEditLease: async (orderItemId: string, leaseId: string): Promise<void> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemEditLeaseRelease', { id: orderItemId, leaseId });

    try {
      await axios.delete(url, buildRequestConfig(config));

      telemetryService.trackDependency(
        `DELETE_orderItemEditLease_${Date.now()}`,
        'DELETE',
        url,
        `/api/orderitems/${orderItemId}/edit-lease/${leaseId}`,
        Date.now() - startTime,
        true,
        204
      );

      telemetryService.trackEvent('OrderItemEditLeaseReleased', {
        orderId: orderItemId
      });
    } catch (error: any) {
      telemetryService.trackDependency(
        `DELETE_orderItemEditLease_${Date.now()}`,
        'DELETE',
        url,
        `/api/orderitems/${orderItemId}/edit-lease/${leaseId}`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Warning, {
        operation: 'releaseOrderItemEditLease',
        orderId: orderItemId,
        statusCode: error.status
      });

      console.error('Error releasing OrderItem edit lease from Order Service', error);
      throw error;
    }
  },

  getCustomerUploadedPlans: async (orderItemId: string): Promise<CustomerUploadedPlansAttachmentDto> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('customerUploadedPlans', { id: orderItemId });

    try {
      const response = await axios.get(url, config);

      telemetryService.trackDependency(
        `GET_customerUploadedPlans_${Date.now()}`,
        'GET',
        url,
        `/api/orderitems/${orderItemId}/attachments/customer-uploaded-plans`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.trackEvent('CustomerUploadedPlansRetrieved', {
        orderId: orderItemId,
        attachmentCount: response.data?.attachmentUris?.length || 0,
        isOverride: response.data?.isOverride || false,
        canReplace: response.data?.canReplace || false
      });

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `GET_customerUploadedPlans_${Date.now()}`,
        'GET',
        url,
        `/api/orderitems/${orderItemId}/attachments/customer-uploaded-plans`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Warning, {
        operation: 'getCustomerUploadedPlans',
        orderId: orderItemId,
        statusCode: error.status
      });

      console.error('Error retrieving customer uploaded plans from Order Service', error);
      throw error;
    }
  },

  replaceCustomerUploadedPlans: async (
    orderItemId: string,
    attachmentName: string,
    attachmentBytes: string,
    contentType: string
  ): Promise<CustomerUploadedPlansAttachmentDto> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('customerUploadedPlans', { id: orderItemId });
    const attachmentSizeBytes = getBase64ByteLength(attachmentBytes);
    const command = {
      OrderItemId: orderItemId,
      AttachmentName: attachmentName,
      AttachmentBytes: attachmentBytes,
      ContentType: contentType
    };

    telemetryService.startTrackEvent('CustomerUploadedPlansReplacement');

    try {
      const response = await axios.put(url, command, config);

      telemetryService.trackDependency(
        `PUT_customerUploadedPlans_${Date.now()}`,
        'PUT',
        url,
        `/api/orderitems/${orderItemId}/attachments/customer-uploaded-plans`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.stopTrackEvent('CustomerUploadedPlansReplacement', {
        orderId: orderItemId,
        fileName: attachmentName,
        contentType,
        fileSizeBytes: attachmentSizeBytes,
        success: true
      });

      telemetryService.trackEvent('CustomerUploadedPlansReplaced', {
        orderId: orderItemId,
        fileName: attachmentName,
        contentType,
        fileSizeKB: Math.round(attachmentSizeBytes / 1024)
      });

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `PUT_customerUploadedPlans_${Date.now()}`,
        'PUT',
        url,
        `/api/orderitems/${orderItemId}/attachments/customer-uploaded-plans`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.stopTrackEvent('CustomerUploadedPlansReplacement', {
        orderId: orderItemId,
        fileName: attachmentName,
        success: false,
        error: error.message
      });

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'replaceCustomerUploadedPlans',
        orderId: orderItemId,
        fileName: attachmentName,
        statusCode: error.status
      });

      console.error('Error replacing customer uploaded plans in Order Service', error);
      throw error;
    }
  },

  getOrderItemAttachments: async (id: string, fileType: AttachmentType): Promise<string[]> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemAttachmentFiles', { id, fileType });

    try {
      const response = await axios.get(url, config);

      telemetryService.trackDependency(
        `GET_attachments_${Date.now()}`,
        'GET',
        url,
        `/api/orderitems/${id}/attachments`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.trackEvent('AttachmentsRetrieved', {
        orderId: id,
        fileType,
        attachmentCount: response.data?.length || 0
      });

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `GET_attachments_${Date.now()}`,
        'GET',
        url,
        `/api/orderitems/${id}/attachments`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Warning, {
        operation: 'getOrderItemAttachments',
        orderId: id,
        fileType,
        statusCode: error.status
      });

      console.error('Error retrieving OrderItem attachments from Order Service', error);
      throw error;
    }
  },

  getAttachmentUrl: async (attachmentName: string): Promise<URL> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('attachmentUrl', { attachmentName });

    try {
      const response = await axios.get(url, config);

      telemetryService.trackDependency(
        `GET_attachmentUrl_${Date.now()}`,
        'GET',
        url,
        '/api/orderitems/attachmentUrl',
        Date.now() - startTime,
        true,
        response.status
      );

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `GET_attachmentUrl_${Date.now()}`,
        'GET',
        url,
        '/api/orderitems/attachmentUrl',
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'getAttachmentUrl',
        attachmentName,
        statusCode: error.status
      });

      console.error('Error retrieving OrderItem attachment URL from Order Service', error);
      throw error;
    }
  },

  postAttachments: async (
    orderItemId: string,
    attachmentName: string,
    attachmentBytes: string,
    fileType: string,
    contentType: string,
    editLeaseId?: string | null
  ) => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemAttachments', { id: orderItemId });

    const addAttachmentCommand = {
      OrderItemId: orderItemId,
      AttachmentName: attachmentName,
      AttachmentBytes: attachmentBytes,
      FileType: fileType,
      ContentType: contentType
    };

    telemetryService.startTrackEvent('FileUpload');

    try {
      const response = await axios.post(url, addAttachmentCommand, buildRequestConfig(config, { editLeaseId }));

      telemetryService.trackDependency(
        `POST_attachment_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/attachments`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.stopTrackEvent('FileUpload', {
        orderId: orderItemId,
        fileName: attachmentName,
        fileType,
        contentType,
        fileSizeBytes: attachmentBytes.length,
        success: true
      });

      telemetryService.trackEvent('AttachmentUploaded', {
        orderId: orderItemId,
        fileName: attachmentName,
        fileType,
        contentType,
        fileSizeKB: Math.round(attachmentBytes.length / 1024)
      });

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `POST_attachment_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/attachments`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.stopTrackEvent('FileUpload', {
        orderId: orderItemId,
        fileName: attachmentName,
        fileType,
        success: false,
        error: error.message
      });

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'postAttachments',
        orderId: orderItemId,
        fileName: attachmentName,
        fileType,
        statusCode: error.status
      });

      console.error('Error posting attachments to Order Service', error);
      throw error;
    }
  },

  deleteAttachment: async (
    orderItemId: string,
    filename: string,
    fileType: AttachmentType = AttachmentType.Plans,
    editLeaseId?: string | null
  ): Promise<void> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemAttachments', { id: orderItemId });

    try {
      await axios.delete(url, {
        ...buildRequestConfig(config, { editLeaseId }),
        params: { filename, fileType }
      });

      telemetryService.trackDependency(
        `DELETE_attachment_${Date.now()}`,
        'DELETE',
        url,
        `/api/orderitems/${orderItemId}/attachments`,
        Date.now() - startTime,
        true,
        204
      );

      telemetryService.trackEvent('AttachmentDeleted', {
        orderId: orderItemId,
        fileName: filename,
        fileType
      });
    } catch (error: any) {
      telemetryService.trackDependency(
        `DELETE_attachment_${Date.now()}`,
        'DELETE',
        url,
        `/api/orderitems/${orderItemId}/attachments`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'deleteAttachment',
        orderId: orderItemId,
        fileName: filename,
        fileType,
        statusCode: error.status
      });

      console.error('Error deleting attachment from Order Service', error);
      throw error;
    }
  },

  postComment: async (orderItemId: string, customerId: string, commentText: string, editLeaseId?: string | null) => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('comments', { id: orderItemId });

    const addCommentCommand = {
      OrderItemId: orderItemId,
      CustomerId: customerId,
      CommentText: commentText
    };

    try {
      await axios.post(url, addCommentCommand, buildRequestConfig(config, { editLeaseId }));

      telemetryService.trackDependency(
        `POST_comment_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/comments`,
        Date.now() - startTime,
        true,
        201
      );

      telemetryService.trackEvent('CommentAdded', {
        orderId: orderItemId,
        customerId,
        commentLength: commentText.length
      });
    } catch (error: any) {
      telemetryService.trackDependency(
        `POST_comment_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/comments`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'postComment',
        orderId: orderItemId,
        customerId,
        statusCode: error.status
      });

      console.error('Error posting Comment to Order Service', error);
      throw error;
    }
  },

  postStatusWithComment: async (
    orderItemId: string,
    customerId: string,
    statusId: string,
    commentText: string,
    editLeaseId?: string | null
  ) => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('comments', { id: orderItemId });

    const addStatusWithCommentCommand = {
      OrderItemId: orderItemId,
      CustomerId: customerId,
      StatusId: statusId,
      CommentText: commentText
    };

    telemetryService.startTrackEvent('StatusChange');

    try {
      const response = await axios.post(url, addStatusWithCommentCommand, buildRequestConfig(config, { editLeaseId }));

      telemetryService.trackDependency(
        `POST_statusWithComment_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/comments`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.stopTrackEvent('StatusChange', {
        orderId: orderItemId,
        statusId,
        withComment: true,
        success: true
      });

      telemetryService.trackEvent('OrderStatusChanged', {
        orderId: orderItemId,
        customerId,
        statusId,
        hasComment: true
      });

      return response.data;
    } catch (error: any) {
      telemetryService.trackDependency(
        `POST_statusWithComment_${Date.now()}`,
        'POST',
        url,
        `/api/orderitems/${orderItemId}/comments`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.stopTrackEvent('StatusChange', {
        orderId: orderItemId,
        statusId,
        success: false,
        error: error.message
      });

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'postStatusWithComment',
        orderId: orderItemId,
        statusId,
        statusCode: error.status
      });

      console.error('Error posting Comment with status to Order Service', error);
      throw error;
    }
  },

  updateOrderItem: async (
    orderItemId: string,
    updates: UpdateOrderItemDto,
    etag?: string | null,
    editLeaseId?: string | null
  ): Promise<UpdateOrderItemResult> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItem', { id: orderItemId });

    const requestConfig = buildRequestConfig(config, { etag, editLeaseId });

    try {
      const response = await axios.put(url, { id: orderItemId, ...updates }, requestConfig);
      const updatedETag = extractETag(response.headers);

      telemetryService.trackDependency(
        `PUT_orderItem_${Date.now()}`,
        'PUT',
        url,
        `/api/orderitems/${orderItemId}`,
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.trackEvent('OrderItemUpdated', {
        orderId: orderItemId,
        hasStatusUpdate: !!updates.statusId,
        hasDataUpdate: !!updates.data,
        usedETag: !!etag,
        usedEditLease: !!editLeaseId,
        hasUpdatedETag: !!updatedETag
      });

      return { etag: updatedETag, lockOwnerId: null };
    } catch (error: any) {
      telemetryService.trackDependency(
        `PUT_orderItem_${Date.now()}`,
        'PUT',
        url,
        `/api/orderitems/${orderItemId}`,
        Date.now() - startTime,
        false,
        error.status || 0
      );

      // Handle 412 Precondition Failed (ETag mismatch)
      if (error.status === 412) {
        telemetryService.trackEvent('OrderConcurrencyConflict', {
          orderId: orderItemId,
          usedETag: !!etag
        });

        telemetryService.trackException(error, SeverityLevel.Warning, {
          operation: 'updateOrderItem',
          orderId: orderItemId,
          statusCode: 412,
          isConcurrencyConflict: true
        });

        console.warn('Concurrency conflict updating OrderItem (ETag mismatch)', error);
        throw error;
      }

      if (error.status === 409) {
        const lockOwnerId = error.lockedBy ?? error.data?.lockedBy ?? error.data?.LockedBy ?? error.LockedBy ?? null;

        telemetryService.trackEvent('OrderLockConflict', {
          orderId: orderItemId,
          lockedBy: lockOwnerId
        });

        if (lockOwnerId) {
          return { etag: null, lockOwnerId };
        }
      }

      telemetryService.trackException(error, error.status === 409 ? SeverityLevel.Warning : SeverityLevel.Error, {
        operation: 'updateOrderItem',
        orderId: orderItemId,
        statusCode: error.status,
        isLockConflict: error.status === 409
      });

      console.error('Error updating OrderItem in Order Service', error);
      throw error;
    }
  },

  exportOrderItemsToCSV: async (params = {}): Promise<Blob> => {
    const startTime = Date.now();
    const { url, config } = apiEndpoint('orderItemsExport');

    telemetryService.startTrackEvent('CSVExport');

    try {
      const response = await axios.get(url, {
        ...config,
        params,
        responseType: 'blob',
        headers: {
          Accept: 'text/csv'
        },
        timeout: 0
      });

      const blob = new Blob([response.data], { type: 'text/csv' });

      telemetryService.trackDependency(
        `GET_export_${Date.now()}`,
        'GET',
        url,
        '/api/orderitems/export',
        Date.now() - startTime,
        true,
        response.status
      );

      telemetryService.stopTrackEvent('CSVExport', {
        success: true,
        fileSizeBytes: blob.size,
        params
      });

      telemetryService.trackEvent('OrdersExported', {
        format: 'CSV',
        fileSizeKB: Math.round(blob.size / 1024),
        filterParams: params
      });

      return blob;
    } catch (error: any) {
      telemetryService.trackDependency(
        `GET_export_${Date.now()}`,
        'GET',
        url,
        '/api/orderitems/export',
        Date.now() - startTime,
        false,
        error.status || 0
      );

      telemetryService.stopTrackEvent('CSVExport', {
        success: false,
        error: error.message
      });

      telemetryService.trackException(error, SeverityLevel.Error, {
        operation: 'exportOrderItemsToCSV',
        params,
        statusCode: error.status
      });

      console.error('Error downloading CSV in OrderItemRepo', error);
      throw error;
    }
  }
};

export default orderRepository;
